import {globalScope} from './globalScope';
import {isNumber} from './numberUtils';
import {isValidString} from './stringUtils';
import {isFunction, isNullish} from './Utils';

/**
 * Get `globalScope.devicePixelRatio` (defaults to `1` if not available)
 */
export const getGlobalDevicePixelRatio = (): number => {
  if (isNullish(globalScope.devicePixelRatio)) {
    return 1;
  }
  return globalScope.devicePixelRatio > 0 ? globalScope.devicePixelRatio : 1;
};

/**
 * Get `globalScope.screen` dimensions with {@link getGlobalDevicePixelRatio} applied
 */
export const getGlobalScreenDimensions = (): {height: number; scale: number; width: number} | undefined => {
  if (isNullish(globalScope.screen)) {
    return undefined;
  }

  if (!isNumber(globalScope.screen.width) || !isNumber(globalScope.screen.height)) {
    return undefined;
  }

  const scale = getGlobalDevicePixelRatio();
  const width = globalScope.screen.width * scale;
  const height = globalScope.screen.height * scale;

  if (!isFinite(width) || !isFinite(height) || width <= 0 || height <= 0) {
    return undefined;
  }

  return {width, height, scale};
};

/**
 * Add global event listener
 *
 * Use this for window-level events that relate to the browser viewport/tab:
 * - resize: viewport size changes
 * - scroll: page scroll (when listening at window level)
 * - load/unload/beforeunload: page lifecycle
 * - focus/blur: window/tab focus state
 * - hashchange/popstate: navigation events
 * - storage: localStorage changes from other tabs
 *
 * Note: Events bubble up from target → document → window, so window receives
 * many events that also fire on document, but not vice versa.
 */
export const addGlobalEventListener = (event: string, handler: EventListenerOrEventListenerObject): void => {
  if (isNullish(globalScope.addEventListener) || !isFunction(globalScope.addEventListener) || isNullish(handler)) {
    return;
  }
  // any-wrap needed to not confirm to type checks
  // we use vendor-prefixed events that are not part of the interface
  globalScope.addEventListener(event, handler);
};

/**
 * Remove global event listener
 *
 * @see addWindowEventListener for details on when to use window vs document event listeners
 */
export const removeGlobalEventListener = (event: string, handler: EventListenerOrEventListenerObject): void => {
  if (
    isNullish(globalScope.removeEventListener) ||
    !isFunction(globalScope.removeEventListener) ||
    isNullish(handler)
  ) {
    return;
  }

  // any-wrap needed to not confirm to type checks
  // we use vendor-prefixed events that are not part of the interface
  globalScope.removeEventListener(event, handler);
};

/**
 * Get `globalScope.location.hostname`
 */
export const getGlobalLocationHostname = (): string | undefined => {
  if (isNullish(globalScope.location)) {
    return undefined;
  }

  if (!isValidString(globalScope.location.hostname)) {
    return undefined;
  }

  return globalScope.location.hostname;
};

/**
 * Get `globalScope.location.pathname`
 */
export const getGlobalLocationPathname = (): string | undefined => {
  if (isNullish(globalScope.location)) {
    return undefined;
  }

  if (!isValidString(globalScope.location.pathname)) {
    return undefined;
  }

  return globalScope.location.pathname;
};

/**
 * Get `globalScope.performance.now()` or `undefined` if not available
 *
 * Note: `performance.now()` is preferred over `Date.now()` for measuring time intervals because it provides higher
 * precision and is not affected by changes to the system clock.
 */
export const getGlobalPerformanceNow = (): DOMHighResTimeStamp | undefined => {
  if (isNullish(globalScope.performance?.now) || !isFunction(globalScope.performance.now)) {
    return undefined;
  }

  // performance.now() returns a DOMHighResTimeStamp, which is a number representing milliseconds with microsecond precision
  return globalScope.performance.now();
};

/**
 * Get from `global object` the WebOS-specific API objects (for WebOS TV support)
 */
export const getGlobalWebOSApi = (): {PalmSystem?: any; webOSSystem?: any} => {
  return {
    webOSSystem: globalScope.webOSSystem,
    PalmSystem: globalScope.PalmSystem,
  };
};

/**
 * Check if WebOS API is available (for WebOS TV support)
 */
export const hasGlobalWebOSApi = (): boolean => {
  const {webOSSystem, PalmSystem} = getGlobalWebOSApi();
  return !isNullish(webOSSystem) || !isNullish(PalmSystem);
};

/**
 * Get `globalScope.navigator.language` (with fallback to `globalScope.navigator.userLanguage` for IE)
 */
export const getGlobalNavigatorLanguage = (): string | undefined => {
  if (isNullish(globalScope.navigator)) {
    return undefined;
  }

  return globalScope.navigator.language || (globalScope.navigator as any).userLanguage;
};

/**
 * Get `globalScope.navigator.product`
 */
export const getGlobalNavigatorProduct = (): string | undefined => {
  if (isNullish(globalScope.navigator)) {
    return undefined;
  }

  // navigator.product is deprecated, but it remains the standard way to detect some environments like React Native
  return globalScope.navigator.product;
};

/**
 * Get `globalScope.navigator.userAgent`
 */
export const getGlobalNavigatorUserAgent = (): string | undefined => {
  if (isNullish(globalScope.navigator) || !isValidString(globalScope.navigator.userAgent)) {
    return undefined;
  }

  return globalScope.navigator.userAgent;
};

/**
 * Get `globalScope.videojs.VERSION`
 */
export const getGlobalVideojsVersion = (): string | undefined => {
  if (isNullish(globalScope.videojs)) {
    return undefined;
  }

  return globalScope.videojs['VERSION'];
};

/**
 * Get `globalScope.THEOplayer.VERSION`
 */
export const getGlobalTheoPlayerVersion = (): string | undefined => {
  if (isNullish(globalScope.THEOplayer)) {
    return undefined;
  }

  return globalScope.THEOplayer.version;
};
